import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import api from '../api/client';
import { useAuth } from '../context/AuthContext';

const PLAN_VACIO = {
  id: '', nombre: '', descripcion: '',
  precio_mensual: 0, precio_anual: 0,
  limite_piscinas: '', limite_usuarios: '', limite_dispositivos: '',
  incluye_parametros: false, incluye_alertas: false,
  incluye_gerencia: false, incluye_api: false,
  activo: true, orden: 0,
};

export default function Planes() {
  const { usuario } = useAuth();
  const [planes, setPlanes] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState(PLAN_VACIO);
  const [guardando, setGuardando] = useState(false);

  if (usuario && usuario.rol !== 'super_admin') {
    return <Navigate to="/" replace />;
  }

  const cargar = async () => {
    setCargando(true);
    setError(null);
    try {
      const { data } = await api.get('/planes?incluirInactivos=true');
      setPlanes(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => { cargar(); }, []);

  const abrirCrear = () => {
    setEditando('nuevo');
    setForm(PLAN_VACIO);
  };

  const abrirEditar = (p) => {
    setEditando(p.id);
    setForm({
      id: p.id,
      nombre: p.nombre,
      descripcion: p.descripcion || '',
      precio_mensual: p.precio_mensual,
      precio_anual: p.precio_anual,
      limite_piscinas: p.limite_piscinas ?? '',
      limite_usuarios: p.limite_usuarios ?? '',
      limite_dispositivos: p.limite_dispositivos ?? '',
      incluye_parametros: p.incluye_parametros,
      incluye_alertas: p.incluye_alertas,
      incluye_gerencia: p.incluye_gerencia,
      incluye_api: p.incluye_api,
      activo: p.activo,
      orden: p.orden,
    });
  };

  const guardar = async () => {
    setGuardando(true);
    try {
      const payload = {
        ...form,
        precio_mensual: Number(form.precio_mensual) || 0,
        precio_anual: Number(form.precio_anual) || 0,
        limite_piscinas: form.limite_piscinas === '' ? null : Number(form.limite_piscinas),
        limite_usuarios: form.limite_usuarios === '' ? null : Number(form.limite_usuarios),
        limite_dispositivos: form.limite_dispositivos === '' ? null : Number(form.limite_dispositivos),
        orden: Number(form.orden) || 0,
      };

      if (editando === 'nuevo') {
        await api.post('/planes', payload);
      } else {
        delete payload.id;
        await api.put(`/planes/${editando}`, payload);
      }
      setEditando(null);
      await cargar();
    } catch (e) {
      alert(e.response?.data?.error || 'Error al guardar');
    } finally {
      setGuardando(false);
    }
  };

  const eliminar = async (p) => {
    const ok = confirm(
      `¿Eliminar el plan "${p.nombre}"?\n\n` +
      `Solo se puede si no hay suscripciones activas usándolo.`
    );
    if (!ok) return;
    try {
      await api.delete(`/planes/${p.id}`);
      await cargar();
    } catch (e) {
      alert(e.response?.data?.error || 'Error al eliminar');
    }
  };

  const setC = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-700">Planes</h1>
          <p className="text-sm text-gray-500 mt-1">
            Catálogo de planes comerciales y sus límites.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={cargar}
            className="px-4 py-2 border border-slate-400 text-slate-700 rounded-lg hover:bg-slate-100 text-sm"
          >
            🔄 Actualizar
          </button>
          <button
            onClick={abrirCrear}
            className="px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-800 text-sm font-medium"
          >
            + Nuevo plan
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-lg mb-4">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        {cargando ? (
          <div className="col-span-full p-12 text-center text-gray-500">Cargando...</div>
        ) : planes.length === 0 ? (
          <div className="col-span-full p-12 text-center text-gray-500">
            No hay planes. Crea el primero con "+ Nuevo plan".
          </div>
        ) : (
          planes.map((p) => (
            <div
              key={p.id}
              className={`bg-white rounded-xl shadow-sm border-2 p-5 ${p.activo ? 'border-transparent' : 'border-gray-200 opacity-60'}`}
            >
              <div className="flex justify-between items-start mb-3">
                <div>
                  <div className="text-xs text-gray-400 uppercase tracking-wide">{p.id}</div>
                  <h3 className="text-xl font-bold text-slate-700">{p.nombre}</h3>
                </div>
                {!p.activo && (
                  <span className="text-[10px] bg-gray-200 text-gray-700 px-2 py-0.5 rounded">
                    INACTIVO
                  </span>
                )}
              </div>

              <p className="text-sm text-gray-500 mb-4 h-10">{p.descripcion || '—'}</p>

              <div className="border-t border-b py-3 mb-4">
                <div className="flex justify-between items-baseline">
                  <span className="text-3xl font-bold text-slate-700">${Number(p.precio_mensual).toFixed(0)}</span>
                  <span className="text-xs text-gray-500">/mes</span>
                </div>
                {Number(p.precio_anual) > 0 && (
                  <div className="text-xs text-green-600 mt-1">
                    Anual: ${Number(p.precio_anual).toFixed(0)} · 2 meses gratis
                  </div>
                )}
              </div>

              <ul className="text-sm space-y-1.5 mb-4">
                <li className="flex justify-between">
                  <span className="text-gray-600">Piscinas</span>
                  <span className="font-semibold text-slate-700">
                    {p.limite_piscinas == null ? '∞' : p.limite_piscinas}
                  </span>
                </li>
                <li className="flex justify-between">
                  <span className="text-gray-600">Usuarios</span>
                  <span className="font-semibold text-slate-700">
                    {p.limite_usuarios == null ? '∞' : p.limite_usuarios}
                  </span>
                </li>
                <li className="flex justify-between">
                  <span className="text-gray-600">Dispositivos</span>
                  <span className="font-semibold text-slate-700">
                    {p.limite_dispositivos == null ? '∞' : p.limite_dispositivos}
                  </span>
                </li>
                {p.incluye_parametros && (
                  <li className="text-green-700 text-xs">✅ Parámetros de agua</li>
                )}
                {p.incluye_alertas && (
                  <li className="text-green-700 text-xs">✅ Alertas por Telegram</li>
                )}
                {p.incluye_gerencia && (
                  <li className="text-green-700 text-xs">✅ Módulo Gerencia</li>
                )}
                {p.incluye_api && (
                  <li className="text-green-700 text-xs">✅ Acceso API</li>
                )}
              </ul>

              <div className="flex gap-2 pt-3 border-t">
                <button
                  onClick={() => abrirEditar(p)}
                  className="flex-1 text-xs py-1.5 border border-slate-300 text-slate-700 rounded hover:bg-slate-50"
                >
                  ✏️ Editar
                </button>
                <button
                  onClick={() => eliminar(p)}
                  className="text-xs px-3 py-1.5 text-red-600 border border-red-300 rounded hover:bg-red-50"
                  title="Eliminar (solo si no se usa)"
                >
                  🗑
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Modal crear/editar */}
      {editando && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60"
          onClick={() => !guardando && setEditando(null)}
        >
          <div
            className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 px-6 py-4 bg-slate-700 text-white flex justify-between items-center">
              <h2 className="text-base font-bold">
                {editando === 'nuevo' ? 'Nuevo plan' : `Editar: ${form.nombre}`}
              </h2>
              <button
                onClick={() => !guardando && setEditando(null)}
                className="w-8 h-8 rounded-full hover:bg-white/10 text-sm"
              >
                ✕
              </button>
            </div>

            <div className="p-6 space-y-4">
              {editando === 'nuevo' && (
                <label className="block">
                  <span className="text-xs font-medium text-slate-700">ID (slug único) *</span>
                  <input
                    type="text"
                    value={form.id}
                    onChange={(e) => setC('id', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                    placeholder="ej: premium, gold, startup"
                    className="mt-1 w-full px-3 py-2 border rounded-lg text-sm font-mono"
                    disabled={guardando}
                  />
                  <span className="text-[11px] text-gray-500">Solo letras minúsculas, números y guiones</span>
                </label>
              )}

              <label className="block">
                <span className="text-xs font-medium text-slate-700">Nombre *</span>
                <input
                  type="text"
                  value={form.nombre}
                  onChange={(e) => setC('nombre', e.target.value)}
                  className="mt-1 w-full px-3 py-2 border rounded-lg text-sm"
                />
              </label>

              <label className="block">
                <span className="text-xs font-medium text-slate-700">Descripción</span>
                <textarea
                  value={form.descripcion}
                  onChange={(e) => setC('descripcion', e.target.value)}
                  rows={2}
                  className="mt-1 w-full px-3 py-2 border rounded-lg text-sm"
                />
              </label>

              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="text-xs font-medium text-slate-700">Precio mensual ($)</span>
                  <input
                    type="number" step="0.01" min="0"
                    value={form.precio_mensual}
                    onChange={(e) => setC('precio_mensual', e.target.value)}
                    className="mt-1 w-full px-3 py-2 border rounded-lg text-sm"
                  />
                </label>
                <label className="block">
                  <span className="text-xs font-medium text-slate-700">Precio anual ($)</span>
                  <input
                    type="number" step="0.01" min="0"
                    value={form.precio_anual}
                    onChange={(e) => setC('precio_anual', e.target.value)}
                    className="mt-1 w-full px-3 py-2 border rounded-lg text-sm"
                  />
                </label>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <label className="block">
                  <span className="text-xs font-medium text-slate-700">Límite piscinas</span>
                  <input
                    type="number" min="1"
                    value={form.limite_piscinas}
                    onChange={(e) => setC('limite_piscinas', e.target.value)}
                    placeholder="Vacío = ∞"
                    className="mt-1 w-full px-3 py-2 border rounded-lg text-sm"
                  />
                </label>
                <label className="block">
                  <span className="text-xs font-medium text-slate-700">Límite usuarios</span>
                  <input
                    type="number" min="1"
                    value={form.limite_usuarios}
                    onChange={(e) => setC('limite_usuarios', e.target.value)}
                    placeholder="Vacío = ∞"
                    className="mt-1 w-full px-3 py-2 border rounded-lg text-sm"
                  />
                </label>
                <label className="block">
                  <span className="text-xs font-medium text-slate-700">Límite dispositivos</span>
                  <input
                    type="number" min="1"
                    value={form.limite_dispositivos}
                    onChange={(e) => setC('limite_dispositivos', e.target.value)}
                    placeholder="Vacío = ∞"
                    className="mt-1 w-full px-3 py-2 border rounded-lg text-sm"
                  />
                </label>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.incluye_parametros}
                    onChange={(e) => setC('incluye_parametros', e.target.checked)}
                    className="w-4 h-4"
                  />
                  <span>Incluye módulo Parámetros</span>
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.incluye_alertas}
                    onChange={(e) => setC('incluye_alertas', e.target.checked)}
                    className="w-4 h-4"
                  />
                  <span>Incluye Alertas (Telegram)</span>
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.incluye_gerencia}
                    onChange={(e) => setC('incluye_gerencia', e.target.checked)}
                    className="w-4 h-4"
                  />
                  <span>Incluye módulo Gerencia</span>
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.incluye_api}
                    onChange={(e) => setC('incluye_api', e.target.checked)}
                    className="w-4 h-4"
                  />
                  <span>Incluye acceso API</span>
                </label>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="text-xs font-medium text-slate-700">Orden</span>
                  <input
                    type="number" min="0"
                    value={form.orden}
                    onChange={(e) => setC('orden', e.target.value)}
                    className="mt-1 w-full px-3 py-2 border rounded-lg text-sm"
                  />
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer mt-5">
                  <input
                    type="checkbox"
                    checked={form.activo}
                    onChange={(e) => setC('activo', e.target.checked)}
                    className="w-4 h-4"
                  />
                  <span className="font-medium">Activo (visible para asignar)</span>
                </label>
              </div>

              <div className="flex justify-end gap-2 pt-3 border-t">
                <button
                  onClick={() => !guardando && setEditando(null)}
                  disabled={guardando}
                  className="px-4 py-2 border rounded-lg text-sm hover:bg-gray-100"
                >
                  Cancelar
                </button>
                <button
                  onClick={guardar}
                  disabled={guardando || !form.id || !form.nombre}
                  className="px-6 py-2 bg-slate-700 text-white rounded-lg text-sm font-medium hover:bg-slate-800 disabled:opacity-50"
                >
                  {guardando ? 'Guardando...' : '💾 Guardar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}